import { z } from 'zod';
import { latitude, longitude, money } from './common.js';
/**
 * Зоны доставки — полигоны GeoJSON в системе координат WGS 84 (EPSG:4326).
 *
 * Порядок координат в GeoJSON — [долгота, широта], а НЕ наоборот.
 * Перепутанные местами координаты дают точку посреди океана, и ошибка при
 * этом не выглядит ошибкой: проверка просто не находит зону.
 */
const position = z.tuple([longitude, latitude]);
/**
 * Линейное кольцо: минимум 4 точки, первая совпадает с последней.
 * Незамкнутое кольцо PostGIS отвергнет уже на уровне CHECK ST_IsValid,
 * но лучше сказать об этом до похода в базу.
 */
const linearRing = z
    .array(position)
    .min(4, 'В контуре должно быть минимум 3 точки')
    // Обводка пальцем без упрощения дала бы сотни точек: такой контур ничего не
    // добавляет к форме зоны, но `ST_Contains` по нему считается на каждом заказе
    .max(500, 'В контуре слишком много точек — упростите его')
    .refine((ring) => {
    const first = ring[0];
    const last = ring[ring.length - 1];
    return first && last && first[0] === last[0] && first[1] === last[1];
}, { message: 'Контур не замкнут: первая и последняя точки должны совпадать' });
export const geoJsonPolygon = z.object({
    type: z.literal('Polygon'),
    // Первое кольцо — внешний контур, последующие — вырезы (дырки)
    coordinates: z.array(linearRing).min(1),
});
export const deliveryZone = z.object({
    id: z.number().int().positive(),
    vendor_id: z.number().int().positive(),
    name_ru: z.string(),
    name_uz: z.string(),
    delivery_price: money,
    is_active: z.boolean(),
    sort_order: z.number().int(),
    geom: geoJsonPolygon,
    /** Площадь контура в км² — по ней видно, квартал это или полгорода. */
    area_km2: z.number(),
    /**
     * Идентификаторы зон ТОЙ ЖЕ витрины, с которыми есть пересечение.
     * Предупреждение, не запрет. Пересечения между витринами не считаются:
     * это штатная ситуация, из неё и берётся список выбора у клиента.
     */
    overlaps: z.array(z.number().int().positive()),
});
export const createZoneBody = z.object({
    /** Витрина-владелец: зона отвечает на вопрос «куда и почём вожу Я». */
    vendor_id: z.number().int().positive(),
    name_ru: z.string().min(1).max(120),
    name_uz: z.string().min(1).max(120),
    delivery_price: money,
    is_active: z.boolean().default(true),
    sort_order: z.number().int().min(0).max(9999).default(0),
    geom: geoJsonPolygon,
});
export const updateZoneBody = createZoneBody.partial();
/** Зона, отдаваемая клиенту при определении адреса: без геометрии. */
export const zoneBrief = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    delivery_price: money,
});
export const adminZonesQuery = z.object({
    vendor_id: z.coerce.number().int().positive().optional(),
});
export const resolveZoneBody = z.object({
    lat: latitude,
    lon: longitude,
});
/**
 * Точка вне зон — это `200` с `covered: false`, а не ошибка: клиент двигает
 * пин по карте, и каждое движение мимо зоны не является ошибочным запросом.
 */
export const resolveZoneResponse = z.object({
    covered: z.boolean(),
    zone: zoneBrief.nullable(),
});
//# sourceMappingURL=zone.js.map